import React, { useState } from 'react';
import {
  Box, Checkbox, IconButton, ListItemIcon, ListItemText, Menu, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Tooltip, Typography, styled, useMediaQuery, useTheme
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/*
  ResponsiveListTable: the shared compact list used by Transactions,
  Budgets and Actions.

  - Desktop (sm and up): a dense table — one row per item, sortable
    column headers, a select-all checkbox in the header, and inline
    action icons on each row.
  - Mobile (below sm): a two-line list — primary text + amount on the
    first line, secondary text + chips on the second, with row actions
    collapsed into a kebab menu. Tapping the row fires onRowClick
    (typically the View dialog) so the tap target stays large.

  Props:
   - items: the current page of items to render
   - getId(item): unique id for selection/keys
   - columns: [{ id, label, sortable=true, align, sx, render(item, expanded) }]
   - sortBy / sortDirection / onSort(columnId): header sorting; headers
     render as plain labels when onSort is missing or a column opts out
   - selectable + selectedIds / onToggleSelect(id) / onSelectAll(checked)
   - isRowSelectable(item): optional; rows it rejects render dimmed with a
     disabled checkbox and are ignored by the select-all state
   - actions: [{ key, label, icon, onClick(item, event), danger }] — inline
     icons on desktop, kebab menu entries on mobile
   - onRowClick(item): mobile row tap; when omitted, tapping a mobile row
     expands it instead (mirroring the desktop click-to-expand)
   - mobilePrimary / mobileSecondary / mobileChips / mobileAmount:
     render functions (item, expanded) for the mobile two-line layout

  Clicking a desktop row (or a mobile row without onRowClick) toggles the
  row's expanded state: truncated cells unwrap onto multiple lines and
  render functions receive expanded=true to reveal any extra detail.
*/

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.secondary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  whiteSpace: 'nowrap',
  fontSize: theme.typography.pxToRem(13)
}));

const BodyCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  whiteSpace: 'nowrap',
  fontSize: theme.typography.pxToRem(13)
}));

const BodyRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected
  },
  transition: 'background-color 0.2s',
  '& .MuiTableCell-root': {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75)
  }
}));

export default function ResponsiveListTable({
  items = [],
  getId = (item) => item._id || item.id,
  columns = [],
  sortBy,
  sortDirection,
  onSort,
  selectable = true,
  isRowSelectable,
  selectedIds = [],
  onToggleSelect,
  onSelectAll,
  actions = [],
  onRowClick,
  mobilePrimary,
  mobileSecondary,
  mobileChips,
  mobileAmount,
  minTableWidth = 680
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuState, setMenuState] = useState(null); // { anchorEl, item }
  // Clicking a row expands it so truncated cells wrap onto multiple lines
  const [expandedIds, setExpandedIds] = useState([]);
  const toggleExpanded = (id) =>
    setExpandedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const isSelected = (item) => selectedIds.includes(getId(item));
  const canSelect = (item) => !isRowSelectable || isRowSelectable(item);
  const selectableItems = items.filter(canSelect);
  const allSelected = selectableItems.length > 0 && selectableItems.every(isSelected);
  const someSelected = selectableItems.some(isSelected);

  const closeMenu = () => setMenuState(null);

  const actionMenu = actions.length > 0 && (
    <Menu anchorEl={menuState?.anchorEl} open={Boolean(menuState)} onClose={closeMenu}>
      {actions.map(action => (
        <MenuItem
          key={action.key}
          onClick={(e) => { const item = menuState?.item; closeMenu(); if (item) action.onClick(item, e); }}
          sx={action.danger ? { color: 'error.main' } : undefined}
        >
          <ListItemIcon sx={action.danger ? { color: 'error.main' } : undefined}>{action.icon}</ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>{action.label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );

  if (isMobile) {
    return (
      <Box>
        {selectable && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5 }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
              inputProps={{ 'aria-label': 'Select all' }}
              sx={{ p: 0.5 }}
            />
            <Typography
              variant="caption"
              onClick={() => onSelectAll && onSelectAll(!allSelected)}
              sx={{ color: 'text.secondary', fontWeight: 500, cursor: 'pointer', userSelect: 'none' }}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Typography>
          </Box>
        )}
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          {items.map(item => {
            const id = getId(item);
            const expanded = expandedIds.includes(id);
            return (
              <Box
                key={id}
                onClick={onRowClick ? () => onRowClick(item) : () => toggleExpanded(id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.75,
                  px: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  opacity: canSelect(item) ? 1 : 0.5,
                  '&:hover': { bgcolor: 'grey.50' }
                }}
              >
                {selectable && (
                  <Checkbox
                    size="small"
                    checked={isSelected(item)}
                    disabled={!canSelect(item)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleSelect && onToggleSelect(id)}
                    inputProps={{ 'aria-label': 'Select row' }}
                    sx={{ p: 0.5 }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography noWrap={!expanded} sx={{ fontWeight: 600, fontSize: '0.8125rem', minWidth: 0, wordBreak: 'break-word' }}>
                      {mobilePrimary && mobilePrimary(item, expanded)}
                    </Typography>
                    {mobileAmount && (
                      <Box sx={{ flexShrink: 0 }}>{mobileAmount(item, expanded)}</Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, minWidth: 0, flexWrap: expanded ? 'wrap' : 'nowrap' }}>
                    {mobileSecondary && (
                      <Typography variant="caption" color="text.secondary" noWrap={!expanded} sx={{ fontSize: '0.7rem', minWidth: 0, wordBreak: 'break-word' }}>
                        {mobileSecondary(item, expanded)}
                      </Typography>
                    )}
                    {mobileChips && (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0, flexWrap: expanded ? 'wrap' : 'nowrap' }}>
                        {mobileChips(item, expanded)}
                      </Box>
                    )}
                  </Box>
                </Box>
                {actions.length > 0 && (
                  <IconButton
                    size="small"
                    aria-label="Row actions"
                    onClick={(e) => { e.stopPropagation(); setMenuState({ anchorEl: e.currentTarget, item }); }}
                    sx={{ p: 0.5, color: 'text.secondary' }}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
        {actionMenu}
      </Box>
    );
  }

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: minTableWidth }}>
        <TableHead>
          <TableRow>
            {selectable && (
              <HeaderCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={(e) => onSelectAll && onSelectAll(e.target.checked)}
                  inputProps={{ 'aria-label': 'Select all' }}
                  sx={{ p: 0.5 }}
                />
              </HeaderCell>
            )}
            {columns.map(col => {
              const sortable = onSort && col.sortable !== false;
              return (
                <HeaderCell
                  key={col.id}
                  align={col.align}
                  sortDirection={sortable && sortBy === col.id ? sortDirection : false}
                >
                  {sortable ? (
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortDirection : 'asc'}
                      onClick={() => onSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </HeaderCell>
              );
            })}
            {actions.length > 0 && <HeaderCell align="right" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => {
            const id = getId(item);
            const expanded = expandedIds.includes(id);
            return (
              <BodyRow
                key={id}
                selected={selectable && isSelected(item)}
                onClick={() => toggleExpanded(id)}
                sx={{ cursor: 'pointer', opacity: canSelect(item) ? 1 : 0.5 }}
              >
                {selectable && (
                  <BodyCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isSelected(item)}
                      disabled={!canSelect(item)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => onToggleSelect && onToggleSelect(id)}
                      inputProps={{ 'aria-label': 'Select row' }}
                      sx={{ p: 0.5 }}
                    />
                  </BodyCell>
                )}
                {columns.map(col => (
                  <BodyCell
                    key={col.id}
                    align={col.align}
                    sx={[
                      col.sx,
                      expanded && { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', wordBreak: 'break-word' }
                    ]}
                  >
                    {col.render(item, expanded)}
                  </BodyCell>
                ))}
                {actions.length > 0 && (
                  <BodyCell align="right">
                    <Box sx={{ display: 'inline-flex', gap: 0 }}>
                      {actions.map(action => (
                        <Tooltip key={action.key} title={action.label}>
                          <IconButton
                            size="small"
                            aria-label={action.label}
                            onClick={(e) => { e.stopPropagation(); action.onClick(item, e); }}
                            sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: action.danger ? 'error.main' : 'primary.main' } }}
                          >
                            {action.icon}
                          </IconButton>
                        </Tooltip>
                      ))}
                    </Box>
                  </BodyCell>
                )}
              </BodyRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
